import React, { useEffect, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Upload, Sparkles, FileText, Loader2, TrendingUp, TrendingDown, Check, AlertTriangle, Calculator } from "lucide-react";

const TAB_UPLOAD = "upload", TAB_ESTIMATE = "estimate", TAB_HISTORY = "history";

export default function BOQ() {
  const [tab, setTab] = useState(TAB_UPLOAD);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { id, items, item_count, total_amount, cost_per_sqft? }
  const [variance, setVariance] = useState(null);
  const [history, setHistory] = useState([]);
  const [file, setFile] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [scope, setScope] = useState("");
  const [area, setArea] = useState("");
  const [floors, setFloors] = useState(1);
  const [ptype, setPtype] = useState("residential");

  const loadHistory = async () => {
    try {
      const { data } = await apiClient().get("/boq/list");
      setHistory(data.boqs || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const uploadBOQ = async () => {
    if (!file) { toast.error("Please choose a PDF or image file"); return; }
    setBusy(true); setResult(null); setVariance(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `/boq/upload?project_name=${encodeURIComponent(projectName)}`;
      const { data } = await apiClient().post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Extracted ${data.item_count} items from BOQ`);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const estimateBOQ = async () => {
    if (!scope.trim()) { toast.error("Describe your project scope"); return; }
    setBusy(true); setResult(null); setVariance(null);
    try {
      const { data } = await apiClient().post("/boq/estimate", {
        scope, area_sqft: area ? Number(area) : null, floors: Number(floors) || 1, project_type: ptype,
      });
      setResult(data);
      toast.success(`AI generated ${data.item_count} line items`);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Estimate failed");
    } finally { setBusy(false); }
  };

  const runAnalysis = async (id) => {
    setBusy(true); setVariance(null);
    try {
      const { data } = await apiClient().post(`/boq/${id}/analyze`);
      setVariance(data);
      toast.success(`Analysis complete — ${data.summary.overruns} overruns detected`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };

  const openBOQ = async (id) => {
    setBusy(true); setVariance(null);
    try {
      const { data } = await apiClient().get(`/boq/${id}`);
      setResult({ id, items: data.items || [], item_count: data.item_count, total_amount: data.total_amount });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 fade-in-up" data-testid="boq-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-primary flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI Feature</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-1 tracking-tight">Drawing & BOQ Intelligence</h1>
        <p className="text-sm text-muted-foreground mt-1">Upload BOQ / drawing → AI extracts items, flags cost overruns, or generates fresh estimates from scope.</p>
      </div>

      {/* Tabs */}
      <div className="inline-flex border border-border overflow-hidden" data-testid="boq-tabs">
        <Tab id={TAB_UPLOAD} cur={tab} setCur={setTab} icon={Upload} label="Upload BOQ" />
        <Tab id={TAB_ESTIMATE} cur={tab} setCur={setTab} icon={Calculator} label="AI Estimator" />
        <Tab id={TAB_HISTORY} cur={tab} setCur={setTab} icon={FileText} label={`History (${history.length})`} />
      </div>

      {/* Upload tab */}
      {tab === TAB_UPLOAD && (
        <div className="border border-border bg-card p-5 sm:p-6 space-y-4" data-testid="boq-upload-panel">
          <Field label="Project name (optional)">
            <input value={projectName} onChange={(e) => setProjectName(e.target.value)} data-testid="boq-project-name"
              className="w-full bg-background border border-border px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:border-primary" />
          </Field>
          <Field label="BOQ file (PDF or image)">
            <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="boq-file-input" className="w-full bg-background border border-border px-3 py-2 text-sm" />
          </Field>
          <button onClick={uploadBOQ} disabled={busy || !file} data-testid="boq-upload-btn"
            className="btn-sharp inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {busy ? "Extracting…" : "Upload & Extract"}
          </button>
        </div>
      )}

      {/* Estimator tab */}
      {tab === TAB_ESTIMATE && (
        <div className="border border-border bg-card p-5 sm:p-6 space-y-4" data-testid="boq-estimate-panel">
          <Field label="Project scope *">
            <textarea rows={3} value={scope} onChange={(e) => setScope(e.target.value)}
              data-testid="boq-scope"
              className="w-full bg-background border border-border px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:border-primary resize-none" />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Built-up area (sqft)">
              <input type="number" value={area} onChange={(e) => setArea(e.target.value)} data-testid="boq-area"
                className="w-full bg-background border border-border px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:border-primary" />
            </Field>
            <Field label="Floors">
              <input type="number" value={floors} onChange={(e) => setFloors(e.target.value)} data-testid="boq-floors"
                className="w-full bg-background border border-border px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:border-primary" />
            </Field>
            <Field label="Type">
              <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="boq-ptype"
                className="w-full bg-background border border-border px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:border-primary">
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="industrial">Industrial</option>
              </select>
            </Field>
          </div>
          <button onClick={estimateBOQ} disabled={busy} data-testid="boq-estimate-btn"
            className="btn-sharp inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Generating…" : "Generate AI Estimate"}
          </button>
        </div>
      )}

      {/* History tab */}
      {tab === TAB_HISTORY && (
        <div className="border border-border bg-card divide-y divide-border" data-testid="boq-history-panel">
          {history.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No BOQs yet. Upload or generate one above.</div>}
          {history.map((b) => (
            <div key={b.id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{b.project_name || b.original_filename || (b.type === "ai_estimate" ? "AI Estimate" : "BOQ")}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                  {b.type === "ai_estimate" ? "AI-generated" : "Uploaded"} · {b.item_count} items · ₹{(b.total_amount || 0).toLocaleString("en-IN")}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openBOQ(b.id)} className="mono text-[10px] uppercase tracking-widest border border-border hover:bg-muted px-3 py-1.5">View</button>
                <button onClick={() => runAnalysis(b.id)} className="mono text-[10px] uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1.5">Analyze</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Result — extracted / generated items */}
      {result && (
        <div className="border border-border bg-card" data-testid="boq-result">
          <div className="p-4 sm:p-5 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Result</div>
              <div className="font-heading text-lg mt-0.5">{result.item_count} items · Total ₹{(result.total_amount || 0).toLocaleString("en-IN")}</div>
              {result.cost_per_sqft && <div className="text-xs text-muted-foreground mt-1">≈ ₹{result.cost_per_sqft.toLocaleString("en-IN")} per sqft</div>}
            </div>
            <button onClick={() => runAnalysis(result.id)} disabled={busy} data-testid="analyze-current-btn"
              className="btn-sharp inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm disabled:opacity-50">
              <TrendingUp className="h-4 w-4" /> Analyze vs Consumption
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="text-left px-4 py-2">Item</th>
                  <th className="text-left px-4 py-2">Unit</th>
                  <th className="text-right px-4 py-2">Qty</th>
                  <th className="text-right px-4 py-2">Rate</th>
                  <th className="text-right px-4 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(result.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="px-4 py-2">{it.item}</td>
                    <td className="px-4 py-2 text-muted-foreground">{it.unit}</td>
                    <td className="px-4 py-2 text-right">{it.quantity}</td>
                    <td className="px-4 py-2 text-right">{it.rate ? `₹${Number(it.rate).toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-4 py-2 text-right font-medium">{it.amount ? `₹${Number(it.amount).toLocaleString("en-IN")}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Variance report */}
      {variance && (
        <div className="border border-border bg-card" data-testid="boq-variance">
          <div className="p-4 sm:p-5 border-b border-border">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Variance Analysis</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <Stat label="Total items" val={variance.summary.total_items} />
              <Stat label="Matched" val={variance.summary.matched_items} icon={<Check className="h-3 w-3" />} />
              <Stat label="Overruns" val={variance.summary.overruns} icon={<AlertTriangle className="h-3 w-3 text-amber-500" />} />
              <Stat label="Under-used" val={variance.summary.underused} icon={<TrendingDown className="h-3 w-3 text-emerald-500" />} />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="text-left px-4 py-2">Item</th>
                  <th className="text-right px-4 py-2">Planned</th>
                  <th className="text-right px-4 py-2">Actual</th>
                  <th className="text-right px-4 py-2">Δ %</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {variance.variances.map((v, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="px-4 py-2">{v.item}</td>
                    <td className="px-4 py-2 text-right">{v.planned} {v.unit}</td>
                    <td className="px-4 py-2 text-right">{v.actual} {v.unit}</td>
                    <td className={`px-4 py-2 text-right font-medium ${v.variance_pct > 10 ? "text-amber-500" : v.variance_pct < -10 ? "text-emerald-500" : ""}`}>{v.variance_pct}%</td>
                    <td className="px-4 py-2 mono text-[10px] uppercase tracking-widest">
                      {v.status === "overrun" ? <span className="text-amber-500">Overrun</span> :
                       v.status === "underuse" ? <span className="text-emerald-500">Under-use</span> :
                       v.matched ? <span className="text-muted-foreground">Ok</span> : <span className="text-muted-foreground">No match</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const Tab = ({ id, cur, setCur, icon: Icon, label }) => (
  <button onClick={() => setCur(id)} data-testid={`tab-${id}`}
    className={`px-4 py-2 text-sm mono uppercase tracking-widest inline-flex items-center gap-2 ${cur === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
    <Icon className="h-3.5 w-3.5" /> {label}
  </button>
);

const Field = ({ label, children }) => (
  <label className="block">
    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">{label}</div>
    {children}
  </label>
);

const Stat = ({ label, val, icon }) => (
  <div className="border border-border rounded-md p-3">
    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">{icon}{label}</div>
    <div className="font-heading text-2xl mt-1">{val}</div>
  </div>
);
